import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Module Registry - manages module registration and provides access to module capabilities
 */
class ModuleRegistry {
    constructor() {
        Object.defineProperty(this, "modules", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "contracts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
    }
    /**
     * Initialize Registry (called in main.ts)
     * @param contracts Shell-provided contracts
     */
    initialize(contracts) {
        this.contracts = contracts;
    }
    /**
     * Register Module
     * @param module Module instance implementing ModuleContract
     */
    registerModule(module) {
        if (!this.contracts) {
            throw new Error("ModuleRegistry not initialized. Call initialize() first.");
        }
        const registration = module.register(this.contracts);
        this.modules.set(registration.id, module);
        logger.debug(`[ModuleRegistry] Registered module: ${registration.name}`);
    }
    /**
     * Get all Module routes
     * @returns Array of route configurations
     */
    getModuleRoutes() {
        const routes = [];
        for (const [id, module] of this.modules) {
            try {
                const moduleRoutes = module.getRoutes();
                routes.push(...moduleRoutes);
                logger.debug(`[ModuleRegistry] Loaded ${moduleRoutes.length} routes from module: ${id}`);
            }
            catch (error) {
                logger.debug(`[ModuleRegistry] Failed to get routes from module ${id}:`, error);
            }
        }
        return routes;
    }
    /**
     * Get all Module navigation items
     * @returns Array of navigation items
     */
    getNavigationItems() {
        const items = [];
        for (const [id, module] of this.modules) {
            try {
                const moduleItems = module.getNavigationItems();
                items.push(...moduleItems);
            }
            catch (error) {
                logger.debug(`[ModuleRegistry] Failed to get navigation items from module ${id}:`, error);
            }
        }
        return items;
    }
    /**
     * Get component for specified Slot
     * @param slotName Slot name (e.g., 'header', 'footer', 'sidebar')
     * @returns Vue component or null
     */
    getComponentForSlot(slotName) {
        for (const [, module] of this.modules) {
            if (module.getComponent) {
                const component = module.getComponent(slotName);
                if (component) {
                    return component;
                }
            }
        }
        return null;
    }
    /**
     * Unregister all Modules
     */
    unregisterAll() {
        for (const [, module] of this.modules) {
            if (module.unregister) {
                module.unregister();
            }
        }
        this.modules.clear();
    }
}
export const moduleRegistry = new ModuleRegistry();
